// ─── src/ChartLabView.tsx ─────────────────────────────────────────────────────
import { useState } from "react";
import { Plus, Trash2, FlaskConical } from "lucide-react";
import { useChartLab }   from "./hooks/useChartLab";
import { DynamicChart }  from "./components/dashboard/DynamicChart";
import type { ChartSource, ChartType } from "./types/chartLab";
import { METRIC_OPTIONS, SOURCE_LABELS } from "./types/chartLab";

const SOURCES: ChartSource[] = [
  "registrations",
  "mentors",
  "report-leads",
  "community-catalysts",
];

export default function ChartLabView() {
  const { charts, addChart, removeChart, clearAll } = useChartLab();

  const [showForm,   setShowForm]   = useState(false);
  const [source,     setSource]     = useState<ChartSource>("registrations");
  const [metric,     setMetric]     = useState<string>(METRIC_OPTIONS["registrations"][0].key);
  const [chartType,  setChartType]  = useState<ChartType>("donut");

  const metricOptions = METRIC_OPTIONS[source];

  const handleSourceChange = (s: ChartSource) => {
    setSource(s);
    setMetric(METRIC_OPTIONS[s][0].key);
  };

  const handleAdd = () => {
    const metricLabel = metricOptions.find((o) => o.key === metric)?.label ?? metric;
    const title = `${SOURCE_LABELS[source]} — ${metricLabel}`;
    addChart(source, metric, chartType, title);
    setShowForm(false);
  };

  return (
    <div className="space-y-6">

      {/* Header row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FlaskConical size={18} className="text-brand-orange" />
          <h2 className="text-white font-semibold">Chart Lab</h2>
          {charts.length > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-brand-border text-brand-muted">
              {charts.length}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {charts.length > 0 && (
            <button
              onClick={clearAll}
              className="btn-ghost text-sm flex items-center gap-1.5 text-red-400
                         hover:text-red-300 border-red-500/30 hover:border-red-400/50"
            >
              <Trash2 size={13} />
              Clear All
            </button>
          )}
          <button
            onClick={() => setShowForm((v) => !v)}
            className="btn-primary text-sm flex items-center gap-1.5"
          >
            <Plus size={14} />
            {showForm ? "Cancel" : "Add Chart"}
          </button>
        </div>
      </div>

      {/* Add chart form */}
      {showForm && (
        <div className="card p-5 border-brand-orange/30">
          <p className="text-white text-sm font-medium mb-4">Configure new chart</p>
          <div className="flex flex-col sm:flex-row gap-3 items-end">

            {/* Source */}
            <div className="flex-1 min-w-0">
              <label className="block text-brand-muted text-xs mb-1.5 uppercase tracking-wider">
                Data Source
              </label>
              <select
                value={source}
                onChange={(e) => handleSourceChange(e.target.value as ChartSource)}
                className="w-full py-2.5 px-3 text-sm bg-brand-navy border border-brand-border
                           rounded-lg text-white focus:outline-none focus:border-brand-orange
                           transition-colors"
              >
                {SOURCES.map((s) => (
                  <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
                ))}
              </select>
            </div>

            {/* Metric */}
            <div className="flex-1 min-w-0">
              <label className="block text-brand-muted text-xs mb-1.5 uppercase tracking-wider">
                Metric
              </label>
              <select
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                className="w-full py-2.5 px-3 text-sm bg-brand-navy border border-brand-border
                           rounded-lg text-white focus:outline-none focus:border-brand-orange
                           transition-colors"
              >
                {metricOptions.map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Chart type */}
            <div className="flex-1 min-w-0">
              <label className="block text-brand-muted text-xs mb-1.5 uppercase tracking-wider">
                Chart Type
              </label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as ChartType)}
                className="w-full py-2.5 px-3 text-sm bg-brand-navy border border-brand-border
                           rounded-lg text-white focus:outline-none focus:border-brand-orange
                           transition-colors"
              >
                <option value="donut">Donut</option>
                <option value="bar">Horizontal Bar</option>
              </select>
            </div>

            {/* Add button */}
            <button
              onClick={handleAdd}
              className="btn-primary text-sm px-6 py-2.5 flex-shrink-0"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Charts grid */}
      {charts.length === 0 ? (
        <div className="card p-16 flex flex-col items-center gap-3 text-center">
          <FlaskConical size={32} className="text-brand-border" />
          <p className="text-brand-muted text-sm">
            No charts yet — click <strong className="text-white">Add Chart</strong> to get started.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {charts.map((config) => (
            <DynamicChart
              key={config.id}
              config={config}
              onRemove={removeChart}
            />
          ))}
        </div>
      )}
    </div>
  );
}
